'use client';

import React from 'react';
import ServiceHero from './ServiceHero';
import ServiceCard from './ServiceCard';
import ServiceComparison from './ServiceComparison';
import CostEstimator from './CostEstimator';
import BeforeAfterGallery from './BeforeAfterGallery';
import DownloadableResources from './DownloadableResources';
import ProcessTimeline from './ProcessTimeline';
import TrustIndicators from './TrustIndicators';
import CTASection from './CTASection';

interface Service {
  id: string;
  title: string;
  description: string;
  features: string[];
  icon: string;
  image: string;
  imageAlt: string;
}

interface ComparisonFeature {
  name: string;
  basic: boolean;
  standard: boolean;
  premium: boolean;
}

interface ServiceOption {
  id: string;
  name: string;
  basePrice: number;
}

interface BeforeAfterProject {
  id: string;
  title: string;
  category: string;
  beforeImage: string;
  beforeAlt: string;
  afterImage: string;
  afterAlt: string;
  description: string;
  duration: string;
  area: string;
}

interface Resource {
  id: string;
  title: string;
  description: string;
  fileType: string;
  fileSize: string;
  icon: string;
}

interface TimelineStep {
  id: string;
  title: string;
  description: string;
  duration: string;
  icon: string;
}

interface TrustIndicator {
  id: string;
  title: string;
  value: string;
  icon: string;
  description: string;
}

const ServicesInteractive: React.FC = () => {
  const heroData = {
    title: "Hizmetlerimiz",
    subtitle: "25 Yıllık Tecrübe ile Güvenli Yapılar",
    description: "Isı yalıtımından çatı uygulamalarına, restorasyondan danışmanlığa kadar profesyonel inşaat çözümleri sunuyoruz."
  };

  const services: Service[] = [
  {
    id: "1",
    title: "Isı Yalıtım (Mantolama) Hizmetleri",
    description: "Konut ve ticari binalar için profesyonel dış cephe yalıtımı ve ısı verimliliği çözümleri sunuyoruz. Modern teknikler ve kaliteli malzemelerle enerji tasarrufu sağlıyoruz.",
    features: [
    "EPS / XPS Mantolama uygulamaları",
    "Fileli sıva sistemleri",
    "Dekoratif dış cephe sıvası",
    "Dış cephe boya uygulamaları",
    "Isı ve ses yalıtımı çözümleri",
    "Enerji verimliliği garantisi"],

    icon: "HomeModernIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc7da223-1769505128089.png",
    imageAlt: "Modern bina dış cephe mantolama ve yalıtım uygulaması"
  },
  {
    id: "2",
    title: "Çatı Uygulamaları ve Çatı İzolasyonu",
    description: "Dayanıklı ve uzun ömürlü çatı çözümleri ile yapılarınızı koruma altına alıyoruz. Su ve ısı izolasyonu ile konforlu yaşam alanları oluşturuyoruz.",
    features: [
    "Yeni çatı yapımı",
    "Kiremit çatı uygulamaları",
    "Sandviç panel çatı sistemleri",
    "Çatı aktarma işleri",
    "Su ve ısı izolasyonu",
    "Tüm çatı izolasyon çözümleri"],

    icon: "HomeIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16673655d-1769505130570.png",
    imageAlt: "Profesyonel çatı yapımı ve izolasyon uygulaması"
  },
  {
    id: "3",
    title: "Restorasyon ve Dış Cephe Yenileme",
    description: "Eski veya yıpranmış binaların yenilenmesi ve güçlendirilmesi konusunda uzmanız. Yapısal bütünlüğü koruyarak modern standartlara kavuşturuyoruz.",
    features: [
    "Dış cephe yenileme işleri",
    "Fileli sıva ve güçlendirme",
    "Cephe onarım ve tamirat",
    "Yapısal yüzey iyileştirme",
    "Eski ve tarihi binalar için restorasyon",
    "Mimari doku koruma"],

    icon: "WrenchScrewdriverIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18050ccb4-1766587781844.png",
    imageAlt: "Tarihi bina dış cephe restorasyonu ve yenileme çalışması"
  },
  {
    id: "4",
    title: "İnşaat Danışmanlık Hizmetleri",
    description: "İnşaat uygulamaları öncesi ve sırasında profesyonel danışmanlık hizmeti sunuyoruz. Doğru malzeme seçimi ve uygulama yöntemi ile projenizi başarıya ulaştırıyoruz.",
    features: [
    "Keşif ve teknik inceleme",
    "Uygulama yöntemi danışmanlığı",
    "Malzeme seçimi danışmanlığı",
    "Maliyet ve süre analizi",
    "Uygulama öncesi yol haritası",
    "Teknik destek ve takip"],

    icon: "ChatBubbleLeftRightIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b45b9061-1768712546438.png",
    imageAlt: "İnşaat danışmanlık hizmeti ve teknik planlama"
  },
  {
    id: "5",
    title: "Proje Yönetimi",
    description: "İnşaat projelerinizin tüm süreçlerini profesyonel ekibimizle yönetiyor, zamanında ve bütçe dahilinde tamamlanmasını sağlıyoruz.",
    features: [
    "Kapsamlı proje planlaması",
    "Zaman ve maliyet yönetimi",
    "Tedarikçi ve taşeron koordinasyonu",
    "Kalite güvence sistemleri",
    "Risk yönetimi ve çözümleri",
    "Düzenli ilerleme raporları"],

    icon: "ClipboardDocumentCheckIcon",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_190cfeb43-1766827177254.png",
    imageAlt: "Project manager with hard hat reviewing construction progress on tablet at building site"
  },
  {
    id: "6",
    title: "Özel Çözümler",
    description: "Standart hizmetlerimizin dışında kalan özel ihtiyaçlarınız için kişiye özel çözümler geliştiriyoruz.",
    features: [
    "Özel tasarım projeleri",
    "Akıllı bina sistemleri",
    "Enerji verimliliği çözümleri",
    "Sürdürülebilir yapı teknolojileri",
    "Özel malzeme uygulamaları",
    "İnovatif yapım teknikleri"],

    icon: "SparklesIcon",
    image: "https://images.unsplash.com/photo-1633017752090-b90cc6e0c590",
    imageAlt: "Unique modern architectural design featuring curved glass facade and innovative building materials"
  }];


  const comparisonFeatures: ComparisonFeature[] = [
  { name: "Proje Tasarımı", basic: false, standard: true, premium: true },
  { name: "Yapı Ruhsatı İşlemleri", basic: false, standard: true, premium: true },
  { name: "Kalite Kontrol", basic: true, standard: true, premium: true },
  { name: "Malzeme Garantisi", basic: true, standard: true, premium: true },
  { name: "İşçilik Garantisi (Yıl)", basic: true, standard: true, premium: true },
  { name: "Proje Yöneticisi", basic: false, standard: true, premium: true },
  { name: "Haftalık İlerleme Raporu", basic: false, standard: true, premium: true },
  { name: "İç Mimari Danışmanlık", basic: false, standard: false, premium: true },
  { name: "Peyzaj Tasarımı", basic: false, standard: false, premium: true },
  { name: "Akıllı Ev Sistemleri", basic: false, standard: false, premium: true },
  { name: "Enerji Verimliliği Sertifikası", basic: false, standard: false, premium: true },
  { name: "5 Yıl Bakım Desteği", basic: false, standard: false, premium: true }];


  const serviceOptions: ServiceOption[] = [
  { id: "1", name: "Yeni İnşaat Projeleri", basePrice: 2500 },
  { id: "2", name: "Renovasyon & Restorasyon", basePrice: 1800 },
  { id: "3", name: "Anahtar Teslim Projeler", basePrice: 3200 },
  { id: "4", name: "İç Mekan Tasarımı", basePrice: 1200 },
  { id: "5", name: "Dış Cephe Yenileme", basePrice: 800 }];


  const beforeAfterProjects: BeforeAfterProject[] = [
  {
    id: "1",
    title: "Villa Renovasyonu - Bodrum",
    category: "Renovasyon",
    beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1700d6459-1767782274826.png",
    beforeAlt: "Old weathered villa exterior with damaged walls and overgrown garden before renovation",
    afterImage: "https://images.unsplash.com/photo-1617052167777-f0f26b5c54f4",
    afterAlt: "Beautifully renovated modern villa with white walls, large windows and landscaped garden",
    description: "Eski bir villayı modern yaşam standartlarına kavuşturduk. Yapısal güçlendirme, tam yalıtım ve çağdaş tasarım ile yeni bir yaşam alanı yarattık.",
    duration: "6 Ay",
    area: "280 m²"
  },
  {
    id: "2",
    title: "Ofis Binası Dönüşümü - İstanbul",
    category: "Ticari",
    beforeImage: "https://images.unsplash.com/photo-1554209001-e49526c0a50d",
    beforeAlt: "Outdated office building with old facade and worn exterior before transformation",
    afterImage: "https://images.unsplash.com/photo-1622221236745-db12a297ddcb",
    afterAlt: "Modern glass-fronted office building with contemporary design and LED lighting",
    description: "Eski bir ofis binasını modern iş merkezi haline getirdik. Enerji verimliliği, akıllı sistemler ve çağdaş tasarım ile değer kattık.",
    duration: "8 Ay",
    area: "1.200 m²"
  },
  {
    id: "3",
    title: "Tarihi Konak Restorasyonu - Ankara",
    category: "Restorasyon",
    beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1f8246d0a-1764991082109.png",
    beforeAlt: "Historic mansion with crumbling walls and damaged traditional architecture before restoration",
    afterImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1277ef457-1769463283071.png",
    afterAlt: "Restored historic mansion preserving original Ottoman architecture with modern safety standards",
    description: "Tarihi bir konağı özgün dokusuna sadık kalarak restore ettik. Geleneksel mimari korunurken modern konfor standartları sağlandı.",
    duration: "12 Ay",
    area: "450 m²"
  }];


  const resources: Resource[] = [
  {
    id: "1",
    title: "İnşaat Planlama Rehberi",
    description: "Yeni inşaat projesi için adım adım planlama kılavuzu ve kontrol listeleri",
    fileType: "PDF",
    fileSize: "2.5 MB",
    icon: "DocumentTextIcon"
  },
  {
    id: "2",
    title: "Malzeme Seçim Kataloğu",
    description: "Kaliteli inşaat malzemeleri kataloğu ve karşılaştırma tabloları",
    fileType: "PDF",
    fileSize: "4.8 MB",
    icon: "Square3Stack3DIcon"
  },
  {
    id: "3",
    title: "Bütçe Hesaplama Şablonu",
    description: "Excel formatında detaylı inşaat bütçe hesaplama aracı",
    fileType: "XLSX",
    fileSize: "1.2 MB",
    icon: "CalculatorIcon"
  },
  {
    id: "4",
    title: "Bakım ve Onarım Kılavuzu",
    description: "Yapı bakımı için periyodik kontrol ve bakım önerileri",
    fileType: "PDF",
    fileSize: "1.8 MB",
    icon: "WrenchScrewdriverIcon"
  }];


  const timelineSteps: TimelineStep[] = [
  {
    id: "1",
    title: "İlk Görüşme & Keşif",
    description: "Projenizi dinliyor, ihtiyaçlarınızı analiz ediyor ve ücretsiz keşif yapıyoruz. Bütçe ve zaman planlaması oluşturuyoruz.",
    duration: "1 Hafta",
    icon: "ChatBubbleLeftRightIcon"
  },
  {
    id: "2",
    title: "Tasarım & Planlama",
    description: "Mimari tasarım, statik hesaplamalar ve detaylı proje çizimleri hazırlanıyor. Ruhsat işlemleri başlatılıyor.",
    duration: "2-4 Hafta",
    icon: "PencilSquareIcon"
  },
  {
    id: "3",
    title: "Ruhsat & İzinler",
    description: "Tüm yasal izinler ve yapı ruhsatı alınıyor. Belediye ve ilgili kurumlarla koordinasyon sağlanıyor.",
    duration: "2-6 Hafta",
    icon: "DocumentCheckIcon"
  },
  {
    id: "4",
    title: "Temel & Kaba İnşaat",
    description: "Zemin hazırlığı, temel atma ve kaba inşaat işleri gerçekleştiriliyor. Yapısal sistemler tamamlanıyor.",
    duration: "2-4 Ay",
    icon: "BuildingOffice2Icon"
  },
  {
    id: "5",
    title: "İnce İşler & Tesisat",
    description: "Elektrik, su, doğalgaz tesisatları ve iç mimari işler yapılıyor. Yalıtım ve kaplama işlemleri tamamlanıyor.",
    duration: "1-2 Ay",
    icon: "WrenchScrewdriverIcon"
  },
  {
    id: "6",
    title: "Son Rötuşlar & Teslim",
    description: "Boya, dekorasyon ve peyzaj işleri tamamlanıyor. Kalite kontrolleri yapılıp anahtar teslim ediliyor.",
    duration: "2-4 Hafta",
    icon: "CheckBadgeIcon"
  }];


  const trustIndicators: TrustIndicator[] = [
  {
    id: "1",
    title: "Yıllık Tecrübe",
    value: "25+",
    icon: "TrophyIcon",
    description: "Çeyrek asırlık sektör deneyimi"
  },
  {
    id: "2",
    title: "Tamamlanan Proje",
    value: "500+",
    icon: "BuildingOffice2Icon",
    description: "Başarıyla teslim edilen projeler"
  },
  {
    id: "3",
    title: "Memnun Müşteri",
    value: "98%",
    icon: "FaceSmileIcon",
    description: "Müşteri memnuniyet oranı"
  },
  {
    id: "4",
    title: "Uzman Ekip",
    value: "150+",
    icon: "UserGroupIcon",
    description: "Profesyonel çalışan sayısı"
  }];


  return (
    <>
      <ServiceHero data-component-id="src/app/services/components/ServicesInteractive.tsx:356:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="356" data-component-end-line="356" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceHero" data-component-content="%7B%22elementName%22%3A%22ServiceHero%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...heroData} />
      
      <section data-component-id="src/app/services/components/ServicesInteractive.tsx:358:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="358" data-component-end-line="375" data-component-file="ServicesInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-16%20px-6%20lg%3Apx-12%20bg-background%22%7D" className="py-16 px-6 lg:px-12 bg-background">
        <div data-component-id="src/app/services/components/ServicesInteractive.tsx:359:8" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="359" data-component-end-line="374" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%22%7D" className="max-w-7xl mx-auto">
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:360:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="360" data-component-end-line="367" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
            <h2 data-component-id="src/app/services/components/ServicesInteractive.tsx:361:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="361" data-component-end-line="363" data-component-file="ServicesInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-3xl%20md%3Atext-4xl%20font-bold%20font-headline%20text-foreground%20mb-4%22%2C%22textContent%22%3A%22Hizmet%20Portf%C3%B6y%C3%BCm%C3%BCz%22%7D" className="text-3xl md:text-4xl font-bold font-headline text-foreground mb-4">
              Hizmet Portföyümüz
            </h2>
            <p data-component-id="src/app/services/components/ServicesInteractive.tsx:364:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="364" data-component-end-line="366" data-component-file="ServicesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20font-body%20text-muted-foreground%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Her%20t%C3%BCrl%C3%BC%20in%C5%9Faat%20ihtiyac%C4%B1n%C4%B1z%20i%C3%A7in%20profesyonel%20%C3%A7%C3%B6z%C3%BCmler%22%7D" className="text-lg font-body text-muted-foreground max-w-2xl mx-auto">
              Her türlü inşaat ihtiyacınız için profesyonel çözümler
            </p>
          </div>
          
          <div data-component-id="src/app/services/components/ServicesInteractive.tsx:369:10" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="369" data-component-end-line="373" data-component-file="ServicesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) =>
            <ServiceCard data-component-id="src/app/services/components/ServicesInteractive.tsx:371:12" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="371" data-component-end-line="371" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceCard" data-component-content="%7B%22elementName%22%3A%22ServiceCard%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" key={service.id} {...service} />
            )}
          </div>
        </div>
      </section>

      <ServiceComparison data-component-id="src/app/services/components/ServicesInteractive.tsx:377:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="377" data-component-end-line="377" data-component-file="ServicesInteractive.tsx" data-component-name="ServiceComparison" data-component-content="%7B%22elementName%22%3A%22ServiceComparison%22%7D" features={comparisonFeatures} />
      <CostEstimator data-component-id="src/app/services/components/ServicesInteractive.tsx:378:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="378" data-component-end-line="378" data-component-file="ServicesInteractive.tsx" data-component-name="CostEstimator" data-component-content="%7B%22elementName%22%3A%22CostEstimator%22%7D" services={serviceOptions} />
      <ProcessTimeline data-component-id="src/app/services/components/ServicesInteractive.tsx:379:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="379" data-component-end-line="379" data-component-file="ServicesInteractive.tsx" data-component-name="ProcessTimeline" data-component-content="%7B%22elementName%22%3A%22ProcessTimeline%22%7D" steps={timelineSteps} />
      <BeforeAfterGallery data-component-id="src/app/services/components/ServicesInteractive.tsx:380:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="380" data-component-end-line="380" data-component-file="ServicesInteractive.tsx" data-component-name="BeforeAfterGallery" data-component-content="%7B%22elementName%22%3A%22BeforeAfterGallery%22%7D" projects={beforeAfterProjects} />
      <TrustIndicators data-component-id="src/app/services/components/ServicesInteractive.tsx:381:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="381" data-component-end-line="381" data-component-file="ServicesInteractive.tsx" data-component-name="TrustIndicators" data-component-content="%7B%22elementName%22%3A%22TrustIndicators%22%7D" indicators={trustIndicators} />
      <DownloadableResources data-component-id="src/app/services/components/ServicesInteractive.tsx:382:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="382" data-component-end-line="382" data-component-file="ServicesInteractive.tsx" data-component-name="DownloadableResources" data-component-content="%7B%22elementName%22%3A%22DownloadableResources%22%7D" resources={resources} />
      <CTASection data-component-id="src/app/services/components/ServicesInteractive.tsx:383:6" data-component-path="src/app/services/components/ServicesInteractive.tsx" data-component-line="383" data-component-end-line="383" data-component-file="ServicesInteractive.tsx" data-component-name="CTASection" data-component-content="%7B%22elementName%22%3A%22CTASection%22%7D" />
    </>);

};

export default ServicesInteractive;